import React, { useMemo } from "react";
import { format, isFuture, isPast, startOfDay, isBefore } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, MapPin, User, Edit, Trash2, FileText, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { entities } from "@/api/apiClient";

function parseDataInicioEvento(e) {
  const d = e?.dataInicio ?? e?.data_inicio;
  if (!d || typeof d !== "string") return null;
  try {
    return safeParseISO(d);
  } catch {
    return null;
  }
}

/** Inclui o dia de hoje (calendário) e datas futuras; exclui apenas dias anteriores a hoje. */
function isHojeOuPosterior(parsed) {
  if (!parsed) return false;
  return !isBefore(startOfDay(parsed), startOfDay(new Date()));
}

export default function VisaoEditais({ eventos, onEdit, onDelete, getEventColorClass, getStatusBadge }) {
  const { data: editais = [] } = useQuery({
    queryKey: ["editais-agenda"],
    queryFn: () => entities.Edital.list(),
  });

  const eventosHojeOuFuturos = useMemo(
    () => eventos.filter((e) => isHojeOuPosterior(parseDataInicioEvento(e))),
    [eventos]
  );

  // Agrupar eventos por edital (apenas data atual e posteriores)
  const eventosPorEdital = useMemo(() => {
    const eventosComEdital = eventosHojeOuFuturos.filter(e => (e.editalId ?? e.edital_id));
    const grupos = {};

    eventosComEdital.forEach(evento => {
      if (!grupos[(evento.editalId ?? evento.edital_id)]) {
        grupos[(evento.editalId ?? evento.edital_id)] = [];
      }
      grupos[(evento.editalId ?? evento.edital_id)].push(evento);
    });

    // Ordenar eventos dentro de cada grupo por data
    Object.keys(grupos).forEach(editalId => {
      grupos[editalId].sort((a, b) => new Date(a.dataInicio ?? a.data_inicio) - new Date(b.dataInicio ?? b.data_inicio));
    });

    return grupos;
  }, [eventosHojeOuFuturos]);

  const getDataEvento = (e) => e?.dataInicio ?? e?.data_inicio;

  // Eventos sem edital: mesma regra (hoje em diante, por data de calendário)
  const eventosSemEdital = useMemo(() => {
    return eventosHojeOuFuturos
      .filter(e => !(e.editalId ?? e.edital_id))
      .sort((a, b) => new Date(getDataEvento(a) || 0) - new Date(getDataEvento(b) || 0));
  }, [eventosHojeOuFuturos]);

  const getEdital = (editalId) => {
    return editais.find(e => e.id === editalId);
  };

  const getProximoEvento = (eventosEdital) => {
    return eventosEdital.find(e => {
      const d = getDataEvento(e);
      const parsed = d && typeof d === 'string' ? safeParseISO(d) : null;
      return parsed && isFuture(parsed);
    }) || eventosEdital[0];
  };

  const contarEventosFuturos = (eventosEdital) => {
    return eventosEdital.filter(e => {
      const d = getDataEvento(e);
      const parsed = d && typeof d === 'string' ? safeParseISO(d) : null;
      return parsed && isFuture(parsed);
    }).length;
  };

  const renderEvento = (evento) => {
    const statusBadge = getStatusBadge(evento.status);
    const dataEvento = getDataEvento(evento);
    const parsedData = dataEvento && typeof dataEvento === 'string' ? safeParseISO(dataEvento) : null;
    const isPassado = parsedData && isPast(parsedData) && evento.status !== "Concluído";
    
    return (
      <Card key={evento.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-start gap-3 flex-1">
              <div className={`w-1 h-full rounded-full ${getEventColorClass(evento.tipo_evento)}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-slate-900">{evento.titulo}</h4>
                  {isPassado && (
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    {parsedData ? format(parsedData, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '-'}
                  </span>
                </div>
                <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                <Badge variant="outline" className="ml-2 text-xs">
                  {evento.tipo_evento}
                </Badge>
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(evento)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onDelete(evento)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {evento.descricao && (
            <p className="text-sm text-slate-600 mb-2 line-clamp-2">{evento.descricao}</p>
          )}

          {evento.local && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <MapPin className="w-3.5 h-3.5" />
              <span>{evento.local}</span>
            </div>
          )}

          {evento.responsavel_nome && (
            <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
              <User className="w-3.5 h-3.5" />
              <span>{evento.responsavel_nome}</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (Object.keys(eventosPorEdital).length === 0 && eventosSemEdital.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum evento relacionado a editais</h3>
        <p className="text-slate-600">Vincule eventos aos editais para visualizá-los aqui</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Eventos agrupados por edital */}
      {Object.entries(eventosPorEdital).map(([editalId, eventosEdital]) => {
        const edital = getEdital(editalId);
        const proximoEvento = getProximoEvento(eventosEdital);
        const eventosFuturos = contarEventosFuturos(eventosEdital);

        return (
          <Card key={editalId} className="border-2 border-blue-100">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg">
                      {edital?.titulo_licitacao || edital?.instituicao || "Edital"}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <Badge variant="outline">
                      {eventosEdital.length} evento(s)
                    </Badge>
                    {eventosFuturos > 0 && (
                      <Badge className="bg-green-100 text-green-800">
                        {eventosFuturos} próximo(s)
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {eventosEdital.map(evento => renderEvento(evento))}
            </CardContent>
          </Card>
        );
      })}

      {/* Eventos futuros sem edital */}
      {eventosSemEdital.length > 0 && (
        <Card className="border-2 border-slate-100">
          <CardHeader className="bg-slate-50 pb-3">
            <CardTitle className="text-lg">Próximos Eventos (Sem Edital)</CardTitle>
            <p className="text-sm text-slate-600">{eventosSemEdital.length} evento(s)</p>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {eventosSemEdital.map(evento => renderEvento(evento))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}